import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {
  ReadOnlyStaticComponent
} from '../../../../../components/form-inputs/read-only-static/read-only-static.component';
import {ParseIsoPipeModule} from 'ngx-date-fns';
import {AuthenticatedBaseComponent} from '../../../../authenticated-base.component';
import {ContentHeaderService} from '../../../../../services/content-header.service';
import {TitleService} from '../../../../../services/title.service';
import {InquiryService} from '../../../../../services/api/inquiry.service';
import {UserSessionService} from '../../../../../services/user-session.service';
import {ToastrService} from 'ngx-toastr';
import {Backend, PERMISSION} from '@generated-backend-types';
import {de} from 'date-fns/locale';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {TIME_MODE} from '../../../../../components/form-inputs/date-time-selector/date-time-selector.component';
import {FormatInTimeZonePipe} from '../../../../../pipes/format-in-time-zone.pipe';
import {StrictFormGroup} from '@browsepedia/ng-forms';
import {
  MultiSelectEnumComponent
} from '../../../../../components/form-inputs/multi-select-enum/multi-select-enum.component';
import {FormGroupComponent} from '../../../../../components/form-inputs/form-group/form-group.component';
import {FormSubmitService} from '../../../../../services/form-submit.service';
import {takeUntil} from 'rxjs';
import Inquiry = Backend.App.Resources.Dashboard.Inquiry.Inquiry;
import InquiryStateEnum = Backend.App.Enums.InquiryStateEnum;
import RoleEnum = Backend.App.Enums.Auth.RoleEnum;
import AnswerInquiryData = Backend.App.Data.Dashboard.Inquiry.AnswerInquiryData;
import InquiryData = Backend.App.Data.Dashboard.Inquiry.InquiryData;

@Component({
  selector: 'app-authenticated-patients-edit-inquiries-inquiry',
  templateUrl: './inquiry.component.html',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    ReadOnlyStaticComponent,
    ParseIsoPipeModule,
    FormatInTimeZonePipe,
    MultiSelectEnumComponent,
    FormGroupComponent,
  ],
  providers: [BsModalService],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InquiryComponent extends AuthenticatedBaseComponent {
  @Input({required: true}) public inquiry!: Inquiry;
  @ViewChild('confirmModal') public confirmModal!: TemplateRef<never>;
  public confirmModalRef?: BsModalRef;
  @Output() public inquiryUpdated = new EventEmitter<void>();
  public form: StrictFormGroup<AnswerInquiryData> = this.fb.group({
    state: this.fb.control(undefined as unknown as InquiryStateEnum, [Validators.required]),
    answer: this.fb.control('', [Validators.required]),
  });
  public isWaitingForResponse = false;
  protected readonly InquiryStateEnum = InquiryStateEnum;
  protected readonly dfnsLocale = de;
  protected readonly TIME_MODE = TIME_MODE;

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly fb: NonNullableFormBuilder,
    private readonly inquiryService: InquiryService,
    private readonly userSessionService: UserSessionService,
    private readonly toastrService: ToastrService,
    private readonly bsModalService: BsModalService,
    private readonly formSubmitService: FormSubmitService,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public get canWithdraw(): boolean {
    return this.canActOn(RoleEnum.ADMIN);
  }

  public get canAnswer(): boolean {
    return this.canActOn(RoleEnum.DOCTOR, this.inquiry.doctor_id);
  }

  private canActOn(role: RoleEnum, ownerId?: string): boolean {
    const user = this.userSessionService.user$.value;

    return !!user &&
      this.inquiry.state === InquiryStateEnum.OPEN &&
      this.userSessionService.hasAllRoles([role]) &&
      (ownerId ? ownerId === user.id : true);
  }

  public withdrawInquiry() {
    this.confirmModalRef = this.bsModalService.show(this.confirmModal);
  }

  public closeWithdrawModal(confirmed: boolean) {
    if (confirmed) {
      this.isWaitingForResponse = true;

      this.inquiryService.withdraw(this.inquiry.id).pipe(takeUntil(this.onDestroy$)).subscribe({
        next: () => {
          this.toastrService.success(this.translate.instant('authenticated.patients.edit.inquiries.inquiry.withdraw.success'));
          this.inquiryUpdated.emit();
        },
        error: () => {
          this.toastrService.error(this.translate.instant('authenticated.patients.edit.inquiries.inquiry.withdraw.error'));
          this.isWaitingForResponse = false;
          this.cdr.markForCheck();
        },
        complete: () => {
          this.isWaitingForResponse = false;
          this.cdr.markForCheck();
        }
      });
    }

    this.confirmModalRef?.hide();
  }

  public save() {
    if (!this.canAnswer) return;

    this.isWaitingForResponse = true;

    this.formSubmitService.submit<Inquiry, InquiryData, InquiryService, AnswerInquiryData>({
      form: this.form,
      service: this.inquiryService,
      modelId: this.inquiry.id,
      customFunction: (inquiryId, data) => this.inquiryService.answer(inquiryId, data),
      customFunctionPermission: PERMISSION.INQUIRY_INQUIRY_UPDATE_ALL,
      customFunctionSuccessMessage: this.translate.instant('authenticated.patients.edit.inquiries.inquiry.answer.success'),
      customFunctionErrorMessage: this.translate.instant('authenticated.patients.edit.inquiries.inquiry.answer.error'),
    }).pipe(takeUntil(this.onDestroy$)).subscribe({
      next: () => this.inquiryUpdated.emit(),
      error: () => {
        this.isWaitingForResponse = false;
        this.cdr.markForCheck();
      },
      complete: () => {
        this.isWaitingForResponse = false;
        this.cdr.markForCheck();
      }
    });
  }
}
